import { Box, Divider, Tab, Tabs, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useEntityIsCriticalAsset from "../../../../api/hooks/useEntityIsCriticalAsset";
import { detectionKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import ResizableDrawer from "../../../../components/ResizableDrawer";
import { Toast } from "../../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import { STEPS_TABS } from "../../../../utils/constants";
import DetectionsTab from "../../detections/components/DetectionsTab";
import { getActiveDetections } from "../../detections/components/utils";
import StepDetails from "./StepDetails";
import StepOverviewTab from "./StepOverviewTab";

export default function StepDrawer({
  onStepDrawerClose,
  selectedStepId,
  selectedTab = STEPS_TABS.STEPS_TAB,
  entityTypeFilter = null,
}) {
  const [activeTab, setActiveTab] = useState(selectedTab);
  const [selectedStep, setSelectedStep] = useState(null);
  const colors = useColors();
  const { trackEvent, getDetectionByStepID, fetchStep } = useAPI();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const [assetAffectedByDetection, setAssetAffectedByDetection] =
    useState(null);
  const { entityIsCriticalAsset, getAssetByEntityId } =
    useEntityIsCriticalAsset();
  const handleTrack = (tab) => {
    // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
    const trackMessage = "User opened drawer for step id ";

    trackEvent(trackMessage + selectedStepId);
  };
  useEffect(() => {
    if (selectedStepId) {
      setLoading(true);
      fetchStep(selectedStepId)
        .then((res) => {
          setSelectedStep(res.data);
          setLoading(false);
        })
        .catch((err) => {
          Toast.error("Error fetching step");
          setLoading(false);
        });
    }
  }, [selectedStepId]);

  const viewAttackPaths = () => {
    if (selectedStep) {
      trackEvent(
        "User clicked on View Attack Paths from Step Drawer for step id " +
          selectedStep?.step_id
      );
      navigate("/posture/surface", {
        // TODO - Will be changed to search params instead of state in:
        // https://linear.app/breeze-security/issue/DEV-1963/filter-using-url-search-params-instead-of-state-in-attack-surface
        state: { stepID: selectedStep?.step_id },
      });
    }
  };

  const HeaderStep = () => {
    return (
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: colors.grey_50,
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={2}
          justifyContent={"flex-start"}
        >
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={2}
            justifyContent={"flex-start"}
            width={"80%"}
          >
            <Typography variant="h6">{selectedStep.name}</Typography>
          </Box>
          <Box
            width={"20%"}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-start"
          >
            <Typography variant="h6">#{selectedStep?.step_id}</Typography>
          </Box>
        </Box>
        {/* Overview Table */}
        <StepDetails
          selectedStep={selectedStep}
          viewAttackPaths={viewAttackPaths}
        />
        <Typography variant="body2" mt={1}>
          {selectedStep.storyline}
        </Typography>
        <Divider sx={{ m: "24px 0px " }} />
      </Box>
    );
  };

  const { data: detections, isLoading: isLoadingDetections } = useQuery({
    queryKey: detectionKeys.detectionByStep(selectedStepId),
    queryFn: async () => {
      return await getDetectionByStepID(selectedStepId);
    },
    select: (response) =>
      response?.data.sort((a, b) => {
        const dateA = a.date.timestamp_utc;
        const dateB = b.date.timestamp_utc;
        return dateB - dateA; // Sort in descending order
      }),
    enabled: selectedStepId !== null,
  });

  const involvedCriticalAssets = (activeDetections) => {
    const assets = new Set();
    activeDetections?.forEach((detection) => {
      detection?.affected_entity_ids?.forEach((entity) => {
        if (entityIsCriticalAsset(entity)) {
          const asset = getAssetByEntityId(entity);
          if (asset && asset !== undefined) {
            assets.add(asset);
          }
        }
      });
    });

    return Array.from(assets);
  };

  useEffect(() => {
    if (detections) {
      const assets = involvedCriticalAssets(getActiveDetections(detections));

      if (assets.length > 0) {
        setAssetAffectedByDetection(assets);
      }
    }
  }, [detections]);

  useEffect(() => {
    if (selectedStepId) {
      handleTrack(activeTab);
    }
  }, [activeTab]);

  if (!selectedStep) {
    return null;
  }
  return (
    <ResizableDrawer
      initialWidth={790}
      minWidth={790}
      anchor="right"
      open={selectedStepId !== null}
      onClose={() => {
        trackEvent("User closed drawer");
        onStepDrawerClose();
        setActiveTab(selectedTab);
      }}
      PaperProps={{
        style: {
          backgroundColor: colors.grey_50,
          borderRadius: "8px 0 0 0",
          overflowY: "auto",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            textColor="inherit"
            sx={{
              backgroundColor: colors.primary_6percent,

              "& .MuiTab-root": {
                minWidth: 120,
                textTransform: "none",
                fontWeight: "bold",
                padding: "10px 15px",
              },
              "& .Mui-selected": {
                borderRadius: "8px 8px 0 0",
                boxShadow: "0px -1px 3px 0.5px  " + colors.primary_12percent,
                backgroundColor: colors.grey_50,
              },
            }}
            TabIndicatorProps={{
              style: {
                display: "none",
                backgroundColor: colors.primary_main,
              },
            }}
          >
            <Tab
              value={STEPS_TABS.STEPS_TAB}
              label={
                <Typography
                  variant={
                    activeTab === STEPS_TABS.STEPS_TAB ? "subtitle2" : "body2"
                  }
                >
                  Overview
                </Typography>
              }
              sx={{
                textTransform: "none",

                paddingBottom: "0px",
                marginBottom: "-4px",
              }}
            />

            {detectionsActive && selectedStep?.last_detections.length > 0 && (
              <Tab
                value={STEPS_TABS.DETECTION_TAB}
                label={
                  <Typography
                    variant={
                      activeTab === STEPS_TABS.DETECTION_TAB
                        ? "subtitle2"
                        : "body2"
                    }
                  >
                    Detection
                  </Typography>
                }
                sx={{
                  textTransform: "none",

                  paddingBottom: "0px",
                  marginBottom: "-4px",
                }}
              />
            )}
          </Tabs>
          <Box
            sx={{
              overflowY: "auto",
              height: "calc(100vh - 50px)",
              padding: "0 24px",
              borderRadius: "8px 8px 0 0",
            }}
          >
            {activeTab === STEPS_TABS.STEPS_TAB &&
              (selectedStepId ? (
                <StepOverviewTab
                  selectedStep={selectedStep}
                  handleViewDetections={() => {
                    setActiveTab(STEPS_TABS.DETECTION_TAB);
                  }}
                  entityTypeFilter={entityTypeFilter}
                  assetAffectedByDetection={assetAffectedByDetection}
                  loading={loading}
                  HeaderStep={HeaderStep}
                />
              ) : (
                <LoadingIcon size={16} />
              ))}
            {activeTab === STEPS_TABS.DETECTION_TAB &&
              detectionsActive &&
              selectedStep?.last_detections.length > 0 &&
              (!isLoadingDetections ? (
                detections && (
                  <DetectionsTab detections={detections} Header={HeaderStep} />
                )
              ) : (
                <LoadingIcon size={16} />
              ))}
          </Box>
        </Box>
      </Box>
    </ResizableDrawer>
  );
}
